/**
 * ตั้งค่าผู้ช่วย AI (BYOK) — Owner only. AI layer A14.
 *
 * Owner brings their own API key. Without a key the system stays in rule-only
 * mode (สรุปงานวันนี้ ยังทำงาน) — this page only adds the LLM narration/Q&A on
 * top. The key is write-only: we show the last 4 digits, never the full key.
 */
import { useEffect, useState } from 'react';
import { Save, Plug, Sparkles, Gift } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useAISettings, useUpdateAISettings, useTestAIKey } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { apiFetch } from '../../lib/api-client';

// Only Claude ships today (v2: Claude-first); others are visible but disabled.
const PROVIDERS = [
  { value: 'anthropic', label: 'Claude (Anthropic)', enabled: true },
  { value: 'google', label: 'Gemini (Google) — เร็วๆ นี้', enabled: false },
  { value: 'openai', label: 'ChatGPT (OpenAI) — เร็วๆ นี้', enabled: false },
];
const CLAUDE_MODELS = ['claude-haiku-4-5', 'claude-sonnet-4-6', 'claude-opus-4-7'];

/** Content-only version used by the Settings page (tab: ai). */
export function AISettingsContent() {
  const { data, isLoading, error, refetch } = useAISettings();
  const update = useUpdateAISettings();
  const test = useTestAIKey();
  const toast = useToast();

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('claude-haiku-4-5');
  const [cap, setCap] = useState(2_000_000);
  const [apiKey, setApiKey] = useState('');
  const [askLimit, setAskLimit] = useState(10);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setProvider(data.provider ?? 'anthropic');
    setModel(data.model ?? 'claude-haiku-4-5');
    setCap(data.monthly_token_cap);
    setAskLimit(data.ask_daily_limit ?? 10);
  }, [data]);

  if (isLoading) return <Loading />;
  if (error || !data) return <LoadError error={error} onRetry={() => refetch()} />;

  const usagePct = cap > 0 ? Math.min(100, (data.tokens_used_this_month / cap) * 100) : 0;

  async function handleSave() {
    try {
      await update.mutateAsync({
        enabled,
        provider,
        model,
        monthly_token_cap: cap,
        ask_daily_limit: askLimit,
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      });
      setApiKey('');
      toast.success('บันทึกการตั้งค่าแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleTest() {
    try {
      const res = await test.mutateAsync();
      res.ok ? toast.success(res.message) : toast.error(res.message);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทดสอบไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-4 max-w-lg">
        <div className="card p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-brand-600 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-600">
            ใส่ API key ของบริษัทเองเพื่อเปิดการสรุป/ถาม-ตอบด้วย AI. ถ้าไม่ใส่ key ระบบยังใช้งานแบบ
            “สรุปงานวันนี้” ได้ปกติ (ไม่มีคำบรรยายจาก AI). คิดค่า token จาก key ของคุณเอง.
          </p>
        </div>

        {/* เครดิตทดลองฟรี (seed) — โชว์เฉพาะตอนใช้ key กลางอยู่ */}
        {data.on_seed_trial && (
          <div className="card p-4 border-l-4 border-l-brand-500 flex items-start gap-3">
            <Gift className="w-5 h-5 text-brand-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-sm">กำลังใช้เครดิตทดลองฟรี</p>
              <p className="text-xs text-gray-600 mt-0.5">
                เหลือ ~{data.seed_tokens_remaining.toLocaleString()} โทเค็น · ใช้ key กลางของระบบ (สูงสุด 15 ครั้ง/วัน)
              </p>
              <p className="text-xs text-gray-500 mt-1">ใส่ API key ของบริษัทเองด้านล่างเพื่อใช้ได้ไม่จำกัด</p>
            </div>
          </div>
        )}

        {/* เปิด/ปิด */}
        <div className="card p-4 flex items-center justify-between">
          <div>
            <p className="font-medium">เปิดใช้ผู้ช่วย AI</p>
            <p className="text-xs text-gray-500">ปิดอยู่ = ใช้โหมด rule-only</p>
          </div>
          <button
            onClick={() => setEnabled((v) => !v)}
            className={`relative w-12 h-7 rounded-full transition ${enabled ? 'bg-brand-600' : 'bg-gray-300'}`}
            aria-pressed={enabled}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition ${enabled ? 'left-6' : 'left-1'}`} />
          </button>
        </div>

        {/* provider + model */}
        <div className="card p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">ผู้ให้บริการ AI</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="input mt-1 w-full"
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value} disabled={!p.enabled}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">โมเดล</span>
            {provider === 'anthropic' ? (
              <select value={model} onChange={(e) => setModel(e.target.value)} className="input mt-1 w-full">
                {CLAUDE_MODELS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input value={model} onChange={(e) => setModel(e.target.value)} className="input mt-1 w-full" />
            )}
          </label>
        </div>

        {/* API key */}
        <div className="card p-4 space-y-2">
          <label className="block">
            <span className="text-sm font-medium">API key</span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={data.key_set ? `บันทึกแล้ว ••••${data.key_last4 ?? ''} (เว้นว่างถ้าไม่เปลี่ยน)` : 'วาง API key ที่นี่'}
              className="input mt-1 w-full"
              autoComplete="off"
            />
          </label>
          <p className="text-xs text-gray-500">เก็บแบบเข้ารหัส ไม่แสดงเต็มอีกครั้ง</p>
        </div>

        {/* cap + usage */}
        <div className="card p-4 space-y-2">
          <label className="block">
            <span className="text-sm font-medium">เพดาน token ต่อเดือน</span>
            <input
              type="number"
              value={cap}
              onChange={(e) => setCap(Number(e.target.value))}
              className="input mt-1 w-full"
              min={0}
            />
          </label>
          <div className="text-xs text-gray-600">
            ใช้ไปเดือนนี้ {data.tokens_used_this_month.toLocaleString()} / {cap.toLocaleString()}
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full ${usagePct >= 100 ? 'bg-red-500' : 'bg-brand-gradient'}`} style={{ width: `${usagePct}%` }} />
          </div>
        </div>

        {/* Daily ask quota per employee */}
        <div className="card p-4 space-y-2">
          <label className="block">
            <span className="text-sm font-medium">โควต้าคำถาม/วัน/พนักงาน</span>
            <input
              type="number"
              value={askLimit}
              onChange={(e) => setAskLimit(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              className="input mt-1 w-full"
              min={0}
              max={10000}
            />
          </label>
          <p className="text-xs text-gray-500">
            จำกัดพนักงาน 1 คนถามผู้ช่วย AI ได้ไม่เกินกี่ครั้ง/วัน (รีเซ็ตเที่ยงคืน). ตั้ง 0 = ปิดผู้ช่วย AI ทั้งบริษัท. ค่าเริ่มต้น 10
          </p>
        </div>

        {data.last_error && (
          <div className="card p-3 border-l-4 border-l-red-500 text-sm text-red-700">
            ข้อผิดพลาดล่าสุด: {data.last_error}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={handleSave} disabled={update.isPending} className="btn-cta flex items-center gap-2">
            <Save className="w-4 h-4" /> บันทึก
          </button>
          <button
            onClick={handleTest}
            disabled={test.isPending || !data.key_set}
            className="btn-secondary flex items-center gap-2"
            title={data.key_set ? '' : 'บันทึก key ก่อนจึงทดสอบได้'}
          >
            <Plug className="w-4 h-4" /> {test.isPending ? 'กำลังทดสอบ…' : 'ทดสอบ key'}
          </button>
        </div>

        {/* ทดสอบการ์ดแจ้งเตือนแต่ละ tone */}
        <div className="card p-4 space-y-2">
          <p className="font-medium text-sm">ทดสอบการ์ดแจ้งเตือน</p>
          <p className="text-xs text-gray-500">กดเพื่อส่งการ์ดตัวอย่างเข้า LINE ของคุณ</p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            {([
              { tone: 'ai', label: '✨ AI brand', cls: 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-white' },
              { tone: 'normal', label: '🔵 ปกติ', cls: 'bg-slate-900 text-white' },
              { tone: 'medium', label: '🟡 ปานกลาง', cls: 'bg-amber-500 text-white' },
              { tone: 'urgent', label: '🔴 ด่วน', cls: 'bg-red-600 text-white' },
              { tone: 'emp-checkin', label: '👤 เตือนเช็คอิน', cls: 'bg-amber-500 text-white' },
              { tone: 'emp-clockout', label: '👤 ลืมสแกนออก', cls: 'bg-amber-500 text-white' },
              { tone: 'emp-decision-approved', label: '✅ ลาอนุมัติ', cls: 'bg-slate-900 text-white' },
              { tone: 'emp-decision-rejected', label: '❌ ลาปฏิเสธ', cls: 'bg-red-600 text-white' },
              { tone: 'emp-decision-info', label: '❓ ขอข้อมูลเพิ่ม', cls: 'bg-amber-500 text-white' },
              { tone: 'hr-cutoff', label: '📅 HR cutoff', cls: 'bg-amber-500 text-white' },
              { tone: 'hr-profile-day7', label: '⚠️ HR profile D7', cls: 'bg-amber-500 text-white' },
              { tone: 'hr-profile-day14', label: '🚨 HR profile D14', cls: 'bg-red-600 text-white' },
            ] as const).map((b) => (
              <button
                key={b.tone}
                onClick={async () => {
                  try {
                    await apiFetch(`/ai/briefing/test-push?tone=${b.tone}`, { method: 'POST' });
                    toast.success(`ส่งการ์ด ${b.label} แล้ว`);
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : 'ส่งไม่สำเร็จ');
                  }
                }}
                className={`${b.cls} rounded-md py-2 text-sm font-medium`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
    </div>
  );
}

/** Standalone page kept for legacy /owner/ai (now redirects via Settings). */
export function AISettings() {
  return (
    <AdminLayout title="ตั้งค่าผู้ช่วย AI">
      <AISettingsContent />
    </AdminLayout>
  );
}
